import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactElement } from "react";
import { cn } from "@/shared/utils/cn";

// Family color map locked 2026-08-07 from the Figma badge component set
// 211:8076: Solid = family accent bg + white text; Shaded = family tint bg +
// family text; Gray = neutral statuses and counts.
export const badgeVariants = cva(
  // whitespace-nowrap: the Figma badge set is single-line pills; wrapped text
  // makes rounded-full clip through line corners (seen on /admin/people).
  "inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors",
  {
    variants: {
      variant: {
        default: "border-transparent bg-clm-primary-blue text-white",
        // Static AA pair (issue #81): the semantic --secondary resolves to
        // brand blue (#1893d1-family) in the light theme, where white text
        // measures ~3.4:1. clm-brand-blue-dark is the AA-passing brand-blue
        // step (5.40:1 white text, 2026-08-07 accessibility ruling) and is
        // theme-independent.
        secondary: "border-transparent bg-clm-brand-blue-dark text-white",
        outline: "text-foreground",
        success: "border-transparent bg-alert-green-lighter text-alert-green",
        warning: "border-transparent bg-alert-yellow-lighter text-alert-yellow",
        destructive: "border-transparent bg-alert-red text-white",
        "blue-solid": "border-transparent bg-alert-blue text-white",
        "blue-shaded": "border-transparent bg-alert-blue-light text-alert-blue-dark",
        "green-solid": "border-transparent bg-alert-green text-white",
        "green-shaded": "border-transparent bg-alert-green-lighter text-alert-green",
        "yellow-solid": "border-transparent bg-alert-yellow text-white",
        "yellow-shaded": "border-transparent bg-alert-yellow-lighter text-alert-yellow",
        "red-solid": "border-transparent bg-alert-red text-white",
        "red-shaded": "border-transparent bg-alert-red-lighter text-alert-red-dark",
        "purple-solid": "border-transparent bg-alert-purple text-white",
        "purple-shaded": "border-transparent bg-alert-purple-lighter text-alert-purple",
        gray: "border-transparent bg-off-white text-stone-gray",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps): ReactElement {
  return <span className={cn(badgeVariants({ variant, className }))} {...props} />;
}
